import { Children, Fragment, HTMLAttributes, ReactNode, forwardRef } from "react";

import { cn } from "@lib/utils";

const alignClasses = {
	start: "items-start",
	center: "items-center",
	end: "items-end",
	stretch: "items-stretch",
};

const justifyClasses = {
	start: "justify-start",
	center: "justify-center",
	end: "justify-end",
	stretch: "justify-stretch",
	between: "justify-between",
	around: "justify-around",
	evenly: "justify-evenly",
};

const directionClasses = {
	row: "flex-row",
	column: "flex-col",
	"row-reverse": "flex-row-reverse",
	"column-reverse": "flex-col-reverse",
};

export interface StackProps extends HTMLAttributes<HTMLDivElement> {
	direction?: "row" | "column" | "row-reverse" | "column-reverse";
	spacing?: number | string;
	divider?: ReactNode;
	align?: "start" | "center" | "end" | "stretch";
	justify?: keyof typeof justifyClasses;
	wide?: boolean;
	className?: string;
}

/** Stack is a container component for arranging elements vertically or horizontally based on flexbox.
 * @prop direction - The direction of the stack
 * @prop spacing - Space between children (px). Default: 0
 * @prop divider - Element rendered between each child (e.g. a Divider)
 * @prop align - The alignment of the stack
 * @prop justify - The justification of the stack
 * @prop className - The class name to apply to the stack
 * @prop wide - Whether to make the stack full width
 */
export const Stack = forwardRef<HTMLDivElement, StackProps>(
	(
		{
			direction = "column",
			spacing = 0,
			divider,
			style,
			children,
			align = "start",
			justify = "start",
			wide = false,
			className,
			...rest
		},
		ref,
	) => {
		const gap = typeof spacing === "number" ? `${spacing}px` : spacing;

		const content =
			divider && Children.count(children) > 1
				? Children.toArray(children).flatMap((child, i) =>
						i === 0 ? [child] : [<Fragment key={i}>{divider}</Fragment>, child],
					)
				: children;

		return (
			<div
				ref={ref}
				className={cn(
					"flex",
					wide ? "w-full" : "",
					directionClasses[direction],
					alignClasses[align],
					justifyClasses[justify],
					className,
				)}
				style={{
					gap,
					...style,
				}}
				{...rest}
			>
				{content}
			</div>
		);
	},
);

Stack.displayName = "Stack";
